import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TextFieldProps,
} from '@mui/material';
import AdapterMoment from '@mui/lab/AdapterMoment';
import { Moment } from 'moment';

import { useAppSelector } from '../../../../../../hooks';
import {
  TZoomMeetingRequestRequestedByRoleOption,
  zoomMeetingRequestRequestedByRoleOptions,
} from '../../../../../../constants';
import {
  adminGetZoomMeetingRequests,
  getZoomRequestsState,
  IZoomMeetingRequest,
  resetAdminZoomRequestListData,
  TAdminGetZoomMeetingRequests,
  TZoomMeetingRequestRequestedByRole,
  TZoomMeetingRequestStatus,
  ZOOM_MEETING_REQUEST_STATUSES,
} from '../../../../../../store/V2/ZoomRequestsSlice';
import TableHeadCustom, {
  THeadLabelItem,
} from '../../../../../../components/V2/Table/type1/TableHeadCustom';
import useTable, {
  defaultRowsPerPageOptions,
  TUseTableState,
} from '../../../../../../components/V2/Table/type1/useTable';
import NewRequestTableRow from './components/NewRequestTableRow';
import TablePaginationActions from '../../../../../../components/V2/Table/type1/TablePaginationActions';
import UserListDropdown, {
  TUserSelectOption,
} from '../../../../../../components/Inputs/UserListDropdown';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../components/Inputs';
import TableNoData from '../../../../../../components/V2/Empty/TableNoData';
import TableLoader from '../../../../../../components/Loaders/TableLoader';
import ViewZoomRequestModal from '../../modals/ViewZoomRequestModal';

type TFilterData = {
  limit: number;
  offset: number;
  sortKey: string;
  sortValue: number;
  studentId?: string;
  tutorId?: string;
  requestedByRole?: TZoomMeetingRequestRequestedByRole;
  startDate?: string;
  endDate?: string;
};

type TState = {
  selectedStudent: TUserSelectOption | null;
  selectedTutor: TUserSelectOption | null;
  selectedRequestedByRole: TZoomMeetingRequestRequestedByRoleOption | null;
  startDate: Moment | null;
  endDate: Moment | null;
};

const defaultListLimit = 30;
const defaultOrderBy = 'createdAt';
const defaultOrder = 'desc';
const defaultTableData: TUseTableState = {
  defaultOrderBy,
  defaultOrder,
  defaultDense: false,
  defaultCurrentPage: 0,
  defaultRowsPerPage: defaultRowsPerPageOptions[0],
  defaultSelected: [],
};

const defaultState: TState = {
  selectedStudent: null,
  selectedTutor: null,
  selectedRequestedByRole: null,
  startDate: null,
  endDate: null,
};

const defaultFilterData: TFilterData = {
  limit: defaultListLimit,
  offset: 0,
  sortKey: defaultOrderBy,
  sortValue: defaultOrder === 'desc' ? -1 : 1,
  studentId: undefined,
};

const textFieldProps: TextFieldProps = {
  sx: {
    backgroundColor: '#ffffff',
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const tableHeaderLabels: THeadLabelItem[] = [
  {
    id: 'tutor',
    label: 'Tutor Name',
    sortable: false,
    width: 108,
    maxWidth: 108,
  },
  {
    id: 'student',
    label: 'Student Name',
    sortable: false,
    width: 108,
    maxWidth: 108,
  },
  {
    id: 'createdAt',
    label: 'Requested At',
    sortable: true,
    width: 120,
    maxWidth: 120,
  },
  {
    id: 'start',
    label: 'Schedule Time',
    sortable: true,
    width: 180,
    maxWidth: 180,
    badgeElement: (
      <>
        &nbsp;
        <Chip
          size="small"
          label="Old"
          sx={{
            backgroundColor: '#94A3B8',
            color: '#1E293B',
            fontSize: '10px',
            fontWeight: 500,
            borderRadius: '3px',
            p: '3px 6px 3px 6px',
          }}
        />
      </>
    ),
  },
  {
    id: 'newStart',
    label: 'Schedule Time',
    sortable: true,
    width: 180,
    maxWidth: 180,
    badgeElement: (
      <>
        &nbsp;
        <Chip
          size="small"
          label="New"
          sx={{
            backgroundColor: '#3498DB',
            color: '#F8FAFC',
            fontSize: '10px',
            fontWeight: 500,
            borderRadius: '3px',
            p: '3px 6px 3px 6px',
          }}
        />
      </>
    ),
  },
  {
    id: 'rescheduleType',
    label: 'Reschedule Type',
    sortable: false,
    width: 120,
    maxWidth: 120,
  },
  {
    id: 'reason',
    label: 'Reason',
    sortable: false,
  },
  {
    id: 'action',
    label: 'Action',
    width: 80,
    maxWidth: 80,
    sortable: false,
  },
];

const NewRequestsTab: FunctionComponent = () => {
  const dispatch = useDispatch();

  // Store
  const zoomRequestsState = useAppSelector(getZoomRequestsState);

  // State
  const tableData = useTable(defaultTableData);
  const [state, setState] = useState({ ...defaultState });
  const [filterData, setFilterData] = useState({ ...defaultFilterData });
  const [viewRequestModelData, setViewRequestModelData] = useState<{
    open: boolean;
    request?: IZoomMeetingRequest;
  }>({ open: false });

  let zoomMeetingRequestsList: IZoomMeetingRequest[] = [];
  let totalCount = 0;

  if (zoomRequestsState.adminZoomRequestListData) {
    totalCount = zoomRequestsState.adminZoomRequestListData.totalDocs || 0;

    if (
      zoomRequestsState.adminZoomRequestListData.list &&
      zoomRequestsState.adminZoomRequestListData.list.length
    ) {
      zoomMeetingRequestsList = zoomRequestsState.adminZoomRequestListData.list;
    }
  }

  useEffect(() => {
    getZoomMeetingRequestsList({ ...defaultFilterData });

    return () => {
      dispatch(resetAdminZoomRequestListData());
    };
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const getZoomMeetingRequestsList = async (
    filterDataAttributes: TFilterData
  ) => {
    const apiData: TAdminGetZoomMeetingRequests = {
      limit: filterDataAttributes.limit,
      offset: filterDataAttributes.offset,
      statuses: [
        ZOOM_MEETING_REQUEST_STATUSES.PENDING,
      ] as TZoomMeetingRequestStatus[],
    };

    if (filterDataAttributes.sortKey) {
      apiData.sortKey = filterDataAttributes.sortKey;
    }

    if (filterDataAttributes.sortValue) {
      apiData.sortValue = filterDataAttributes.sortValue;
    }

    if (filterDataAttributes.studentId) {
      apiData.studentId = filterDataAttributes.studentId;
    }

    if (filterDataAttributes.tutorId) {
      apiData.tutorId = filterDataAttributes.tutorId;
    }

    if (filterDataAttributes.requestedByRole) {
      apiData.requestedByRole = filterDataAttributes.requestedByRole;
    }

    if (filterDataAttributes.startDate) {
      apiData.startDate = filterDataAttributes.startDate;
    }

    if (filterDataAttributes.endDate) {
      apiData.endDate = filterDataAttributes.endDate;
    }

    dispatch(adminGetZoomMeetingRequests({ ...apiData }));

    setFilterData({ ...filterDataAttributes });
  };

  const handleSort = (id: string) => {
    let newSortValue = 1;

    if (filterData.sortKey === id && filterData.sortValue === 1) {
      newSortValue = -1;
    }

    getZoomMeetingRequestsList({
      ...filterData,
      sortValue: newSortValue,
      sortKey: id,
    });

    tableData.onSort(id);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => {
    getZoomMeetingRequestsList({
      ...filterData,
      offset: page * filterData.limit,
    });

    tableData.onChangePage(event, page);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    getZoomMeetingRequestsList({
      ...filterData,
      offset: 0,
      limit: parseInt(event.target.value, 10),
    });

    tableData.onChangeRowsPerPage(event);
  };

  return (
    <div>
      <div style={{ padding: '0 20px 20px 20px' }}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Grid container columnSpacing="16px" rowSpacing="20px">
            <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
              <FormControl fullWidth>
                <UserListDropdown
                  selectedItem={state.selectedTutor}
                  handleChange={(e, value) => {
                    updateStateAttributes({
                      selectedTutor: value,
                    });

                    getZoomMeetingRequestsList({
                      ...filterData,
                      tutorId: value ? value.value : undefined,
                    });
                  }}
                  textFieldProps={{
                    label: 'Tutor',
                    placeholder: 'Filter by Tutor',
                    ...textFieldProps,
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
              <FormControl fullWidth>
                <UserListDropdown
                  selectedItem={state.selectedStudent}
                  handleChange={(e, value) => {
                    updateStateAttributes({
                      selectedStudent: value,
                    });

                    getZoomMeetingRequestsList({
                      ...filterData,
                      studentId: value ? value.value : undefined,
                    });
                  }}
                  textFieldProps={{
                    label: 'Student',
                    placeholder: 'Filter by Student',
                    ...textFieldProps,
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
              <FormControl fullWidth>
                <Autocomplete
                  size="small"
                  value={state.selectedRequestedByRole}
                  options={zoomMeetingRequestRequestedByRoleOptions}
                  onChange={(e, value) => {
                    updateStateAttributes({
                      selectedRequestedByRole: value,
                    });

                    getZoomMeetingRequestsList({
                      ...filterData,
                      requestedByRole: value ? value.value : undefined,
                    });
                  }}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.value
                  }
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Requested by"
                      placeholder="Requested by Role"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
              <FormControl fullWidth>
                <DatePicker
                  clearable
                  value={state.startDate}
                  label="Start Date"
                  inputFormat="YYYY-MM-DD"
                  onChange={(date) => {
                    updateStateAttributes({
                      startDate: date,
                    });

                    getZoomMeetingRequestsList({
                      ...filterData,
                      startDate: date
                        ? date.clone().startOf('day').toISOString()
                        : undefined,
                    });
                  }}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      placeholder="Start Date"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
              <FormControl fullWidth>
                <DatePicker
                  clearable
                  value={state.endDate}
                  label="End Date"
                  inputFormat="YYYY-MM-DD"
                  onChange={(date) => {
                    updateStateAttributes({
                      endDate: date,
                    });

                    getZoomMeetingRequestsList({
                      ...filterData,
                      endDate: date
                        ? date.clone().endOf('day').toISOString()
                        : undefined,
                    });
                  }}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      placeholder="End Date"
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </div>
      <TableContainer
        sx={{ position: 'relative', maxHeight: 'calc(100vh - 250px)' }}
      >
        <Table stickyHeader size={tableData.dense ? 'small' : 'medium'}>
          <TableHeadCustom
            order={tableData.order}
            orderBy={tableData.orderBy}
            headLabels={tableHeaderLabels}
            rowCount={zoomMeetingRequestsList.length}
            numSelected={tableData.selected.length}
            onSort={handleSort}
            // onSelectAllRows={(checked) =>
            //   tableData.onSelectAllRows(
            //     checked,
            //     zoomMeetingRequestsList.map((row) => row._id)
            //   )
            // }
          />

          <TableBody>
            {zoomMeetingRequestsList.map((row) => {
              const isSelected = tableData.selected.includes(row._id);
              return (
                <NewRequestTableRow
                  key={row._id}
                  requestData={row}
                  selected={isSelected}
                  onSelectRow={() => tableData.onSelectRow(row._id)}
                  onView={() =>
                    setViewRequestModelData({ open: true, request: row })
                  }
                />
              );
            })}
            {zoomRequestsState.status !== 'loading' &&
            !zoomMeetingRequestsList.length ? (
              <TableNoData title="No new requests are available" />
            ) : null}
            {zoomRequestsState.status === 'loading' &&
            !zoomMeetingRequestsList.length ? (
              <TableLoader />
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={defaultRowsPerPageOptions}
          component="div"
          count={totalCount}
          rowsPerPage={tableData.rowsPerPage}
          page={tableData.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleRowsPerPageChange}
          ActionsComponent={TablePaginationActions}
        />

        <FormControlLabel
          label="Dense"
          sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
          control={
            <Switch
              checked={tableData.dense}
              onChange={tableData.onChangeDense}
            />
          }
        />
      </Box>
      {/* Modals */}
      {viewRequestModelData.open && viewRequestModelData.request ? (
        <ViewZoomRequestModal
          requestId={viewRequestModelData.request._id}
          open={viewRequestModelData.open}
          onClose={() => setViewRequestModelData({ open: false })}
        />
      ) : null}
    </div>
  );
};

export default NewRequestsTab;
